import { useState } from "react";
import SectionBox from "../layout/SectionBox";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Facebook, Youtube } from "lucide-react";
import LogoFooter from "../atoms/LogoFooter";
import LogoFooterKom from "../atoms/LogoFooterKom";
import BackToTopButton from "../atoms/BackToTopButton";

// @ts-ignore
import ReCAPTCHA from "react-google-recaptcha";
import { useSubscriberStore } from "../../store/useSubscriberStore";

export default function Footer() {
  const location = useLocation();

  const currentLang = location.pathname.startsWith("/id") ? "id" : "en";

  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const { subscribe, loading, success, error, reset } =
    useSubscriberStore();

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !captchaToken) return;

    await subscribe(email, captchaToken);

    if (!error) {
      setEmail("");
      setCaptchaToken(null);
    }
  };

  return (
    <main>
      <div className="pt-6">
        <SectionBox
          title=""
          titleAlign="center"
          background="linear-gradient(to bottom right, #ffffff 70%, #123b7c 100%)"
          className="!py-0 !rounded-none"
        >
          <footer className="relative text-gray-800 pb-10 px-6 sm:px-2 lg:px-6 xl:px-14">
            <div className="flex justify-between items-start">
              <LogoFooter className="mb-6" />
              <BackToTopButton />
            </div>

            <div className="border-t border-[#414141]" />

            <div className="grid grid-cols-1 md:grid-cols-3 text-black gap-8 mt-6">
              
              {/* LEFT */}
              <div>
                <LogoFooterKom />
                <p className="text-sm">Monday - Friday</p>
                <p className="text-sm">Working Hour 09.00 - 17.00</p>
                <div className="flex gap-4 text-xl mt-3 items-center">
                  <a href="#" aria-label="Instagram">
                    <Instagram className="w-5 h-5 " />
                  </a>
                  <a href="#" aria-label="Facebook">
                    <Facebook className="w-5 h-5 " />
                  </a>
                  <a href="#" aria-label="YouTube">
                    <Youtube className="w-5 h-5 " />
                  </a>
                  <a href="#" aria-label="TikTok">
                    <img
                      className="w-8 h-8"
                      src="https://static.vecteezy.com/system/resources/thumbnails/018/930/574/small/tiktok-logo-tikok-icon-transparent-tikok-app-logo-free-png.png"
                      alt="TikTok"
                    />
                  </a>
                </div>
              </div>

              {/* MIDDLE */}
              <div>
                <h4 className="text-[#123b7c] mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to={`/${currentLang}`} className="hover:text-secondary">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link to={`/${currentLang}/blog/news`} className="hover:text-secondary">
                      News
                    </Link>
                  </li>
                  <li>
                    <Link to={`/${currentLang}/jobs`} className="hover:text-secondary">
                      Jobs
                    </Link>
                  </li>
                  <li>
                    <Link to={`/${currentLang}/privacy-policy`} className="hover:text-secondary">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to={`/${currentLang}/terms-condition`} className="hover:text-secondary">
                      Terms Of Service
                    </Link>
                  </li>
                </ul>
              </div>

              {/* RIGHT */}
              <div>
                <h4 className="text-[#123b7c] mb-4">Short Story</h4>
                <p className="text-sm mb-6 leading-relaxed">
                  TalentLink is a premier headhunting and talent management firm
                  dedicated to bridging exceptional talent with forward-thinking
                  companies.
                </p>

                <h4 className="text-[#123b7c] mb-2">Subscribe</h4>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="flex">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your Email"
                      required
                      className="p-2 rounded-l-md border border-gray-300 w-full focus:outline-none text-sm"
                    />
                    <button
                      type="submit"
                      disabled={loading || !captchaToken}
                      className="bg-[#ffec89] px-4 py-2 rounded-r-md hover:bg-yellow-400 transition disabled:opacity-50"
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>

                  {/* RECAPTCHA */}
                  <ReCAPTCHA
                    sitekey="6LfPuzcsAAAAAItzND8FHtg9SJvsFhRNDZJmiMnH"
                    onChange={handleCaptchaChange}
                  />

                  {success && (
                    <p className="text-green-600 text-sm">
                      Successfully subscribed!
                    </p>
                  )}

                  {error && (
                    <p className="text-red-600 text-sm">
                      {error}
                    </p>
                  )}
                </form>
              </div>
            </div>

            <div className="border-t border-[#414141] mt-8 pt-4 text-sm text-black">
              <p>© {new Date().getFullYear()}. All Rights Reserved</p>
            </div>
          </footer>
        </SectionBox>
      </div>
    </main>
  );
}
